if (!customElements.get("predictive-search")) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
    }

    get getSectionQuery() {
      let id = this.getAttribute("section-id");
      if (!id || id.length === 0) return "";
      return "&section_id=" + id;
    }

    get getQuery() {
      let terms = this.getAttribute("search-terms");
      if (!terms) return false;

      return window.Shopify.routes.root + "search/suggest?q=" + encodeURIComponent(terms);
    }

    getFullSearchQuery() {
      let terms = this.getAttribute("search-terms");
      if (!terms) return false;

      return window.Shopify.routes.root + "search?q=" + encodeURIComponent(terms);
    }

    loadContent() {
      var requestResponse;
      var resourceType = this.getAttribute("data-type");

      var limit = 10; // current max from api anyways //
      if (this.getAttribute("data-limit")) {
        limit = this.getAttribute("data-limit");
      }

      var fetchURI =
        this.getQuery +
        "&resources[type]=" +
        resourceType +
        "&resources[limit]=" +
        limit +
        this.getSectionQuery;

      if (resourceType == "article") {
        fetchURI =
          this.getFullSearchQuery() +
          "&type=article&" +
          "options%5Bprefix%5D=last" +
          this.getSectionQuery;
      }

      fetch(fetchURI)
        .then((res) => {
          requestResponse = res;
          return res.text();
        })
        .then((text) => {
          var markup = new DOMParser().parseFromString(text, "text/html");
          var section = markup.querySelector(
            ".shopify-section .predictive-search-content"
          );

          this.innerHTML = section.innerHTML;
        });
    }

    connectedCallback() {
      this.loadContent();
    }
  }

  customElements.define("predictive-search", PredictiveSearch);
}

// The overlay's search field. Smart parts (recent searches, query pills, routine line, "Du har købt tidligere") live in
// search-smart.js, loaded on the first focus so no page pays for it up front; this file tells it what happens through
// events on <search-modal>: search:submit and search:pick (a search the visitor chose), search:query (a search starts;
// detail.wait collects promises to wait for), search:results, search:empty.
class SearchForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector("form");
    this.input = this.form.querySelector('input[name="q"]');
    this.domParser = new DOMParser();
    this.activeQuery;
    this.seq = 0;

    // A phone has no room for "Søg efter produkt, hudbehov eller ingrediens": the short wording (owner, 2026-10-26).
    if (this.input && this.input.dataset.placeholderShort && window.matchMedia("(max-width: 660px)").matches) {
      this.input.placeholder = this.input.dataset.placeholderShort;
    }

    if (this.input) {
      this.input.addEventListener(
        "keyup",
        debounce((event) => {
          this.onSearch(event);
        }, 175)
      );

      this.input.addEventListener(
        "change",
        debounce((event) => {
          this.onSearch(event);
        }, 175)
      );

      this.form.addEventListener(
        "reset",
        debounce((event) => {
          this.input.value = "";
          this.activeQuery = "";
          this.input.focus();
        }, 175)
      );

      this.input.addEventListener("focus", (event) => {
        this.loadSmart();
        this.fitViewport();
        this.onSearch(event);
      });

      this.form.addEventListener("submit", () => {
        if (this.input.value.trim()) this.emit("search:submit", { q: this.input.value.trim() });
      });
    }
  }

  get modal() {
    return this.closest("search-modal");
  }

  emit(name, detail) {
    if (this.modal) this.modal.dispatchEvent(new CustomEvent(name, { detail: detail }));
  }

  // Phone keyboards: iOS Safari doesn't shrink the layout viewport (nor 100dvh) when the keyboard opens. The overlay keeps
  // its full height and may run under the keyboard (owner, 2026-10-27); the keyboard's height becomes extra room at the
  // bottom of the scroll area (--searchmodal-kb, search-modal.css), so the last row can always be scrolled up above it.
  fitViewport() {
    var vv = window.visualViewport;
    var modal = this.modal;
    if (!vv || !modal) return;
    if (SearchForm.fitUpdate) return SearchForm.fitUpdate();
    var update = (SearchForm.fitUpdate = function () {
      if (!modal.hasAttribute("open")) return;
      var kb = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
      modal.style.setProperty("--searchmodal-kb", kb + "px");
    });
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    update();
  }

  // search-smart.js, once per page (the overlay's data-smart-src).
  loadSmart() {
    if (SearchForm.smartLoading || !this.modal || !this.modal.dataset.smartSrc) return;
    SearchForm.smartLoading = true;
    var script = document.createElement("script");
    script.src = this.modal.dataset.smartSrc;
    script.async = true;
    document.head.appendChild(script);
  }

  // A pill (popular, recent, suggested): search for its words in the overlay.
  pick(query) {
    this.input.value = query;
    this.emit("search:pick", { q: query });
    this.onSearch();
  }

  onSearch() {
    if (this.input.value.trim().length == 0) {
      this.activeQuery = "";
      this.requestedQuery = "";
      this.seq++;
      if (this.ctrl) this.ctrl.abort();
      this.modal.setAttribute("data-active-mode", "noquery");
      this.emit("search:empty", {});
      return;
    }

    // Back to the words already shown (typed "serum", then "serumx", then "serum" again): drop the request on its way,
    // so its answer can't replace what is shown.
    if (this.input.value == this.activeQuery) {
      if (this.requestedQuery) {
        this.seq++;
        this.requestedQuery = "";
        if (this.ctrl) this.ctrl.abort();
      }
      return;
    }
    // keyup also fires for arrow keys and Enter: no new request while the same words are on their way.
    if (this.input.value != this.requestedQuery) {
      this.searchAPI(this.input.value);
    }
  }

  // A typo with a loose or empty answer: the section names the corrected words (data-search-corrected, from
  // snippets/search-correct.liquid); show the search for those instead, headed "Produkter for …" (2026-10-26).
  correct(text, signal) {
    this.correctedNote = null;
    var marker = this.domParser.parseFromString(text, "text/html").querySelector("[data-search-corrected]");
    if (!marker) return text;
    var words = marker.getAttribute("data-search-corrected");
    var params = new URLSearchParams(this.searchParams);
    params.set("q", words);
    return fetch(window.Shopify.routes.root + "search?" + params.toString(), { signal: signal })
      .then((response) => (response.ok ? response.text() : Promise.reject()))
      .then((corrected) => {
        this.correctedNote = { label: marker.getAttribute("data-label"), words: words };
        return corrected;
      })
      .catch(() => text);
  }

  renderSearch() {
    this.searchResponseParsed = this.domParser.parseFromString(
      this.searchResponse,
      "text/html"
    );

    this.searchReponseContent =
      this.searchResponseParsed.querySelector(".shopify-section").innerHTML;
    // A fresh container each time: the new results count as new content, not as the old list moving when the
    // rows above them change (no layout shift).
    var oldTabs = document.querySelector(".searchmodal-tabs");
    var newTabs = oldTabs.cloneNode(false);
    newTabs.innerHTML = this.searchReponseContent;
    if (this.correctedNote) {
      // "Produkter for <corrected words>" as the section heading (owner, 2026-10-26: no separate line).
      var heading = newTabs.querySelector("[data-search-products-heading]");
      if (heading) {
        heading.textContent = this.correctedNote.label + " ";
        var strong = document.createElement("strong");
        strong.textContent = this.correctedNote.words;
        heading.appendChild(strong);
        heading.setAttribute("data-corrected-words", this.correctedNote.words);
      }
    }
    oldTabs.replaceWith(newTabs);

    // Screen readers hear the outcome once per search: "Vis alle resultater (N)" or the no-results line.
    var status = this.modal && this.modal.querySelector("[data-search-status]");
    if (status) {
      var outcome = newTabs.querySelector("[data-search-all] button, .tab-no-results .text");
      status.textContent = outcome ? outcome.textContent.trim() : "";
    }
  }

  searchAPI(query) {
    this.formData = new FormData(this.form);
    this.searchParams = new URLSearchParams(this.formData);
    this.searchParams.set("section_id", "async-search-modal-products");
    this.searchURL =
      window.Shopify.routes.root + "search?" + this.searchParams.toString();
    // Only the latest answer is shown: a slow reply for "tø" must not replace the one for "tør hud", and its request is
    // cancelled.
    var seq = ++this.seq;
    if (this.ctrl) this.ctrl.abort();
    var ctrl = (this.ctrl = new AbortController());
    this.requestedQuery = query;
    // search-smart.js pushes its own fetches (query suggestions, purchases) into wait; the results show only when
    // those are in too, so the rows above the products don't push them down afterwards (no layout shift).
    var detail = { q: query.trim(), wait: [] };
    this.emit("search:query", detail);

    Promise.all([
      fetch(this.searchURL, { signal: ctrl.signal }).then((response) => response.text()).then((text) => this.correct(text, ctrl.signal)),
      Promise.all(detail.wait).catch(() => {}),
    ])
      .then(([responseText]) => {
        if (seq !== this.seq) return;
        this.activeQuery = query;
        this.searchResponse = responseText;
        this.renderSearch();
        this.modal.setAttribute("data-active-mode", "hasquery");
        this.emit("search:results", { q: query.trim() });
      })
      .catch(() => {})
      .finally(() => {
        if (seq === this.seq) this.requestedQuery = "";
      });
  }

  connectedCallback() {
    if (!this.modal) return;
    // Pills anywhere in the overlay, and a click on a result (the search words count as a chosen search).
    this.modal.addEventListener("click", (event) => {
      var pill = event.target.closest("[data-search-query]");
      if (pill) {
        event.preventDefault();
        this.pick(pill.getAttribute("data-search-query"));
        return;
      }
      var link = event.target.closest(".searchmodal-tabs a[href], .searchmodal-tabs button[type='submit']");
      if (link && this.activeQuery) this.emit("search:pick", { q: this.activeQuery.trim() });
    });
  }
}

customElements.define("search-form", SearchForm);

// The routine help card (snippets/search-help-card.liquid; overlay and search page): with one action, the whole card
// is its hit area; a tap on the card's text or icon opens it. With two actions only the buttons do (2026-10-26).
document.addEventListener("click", function (event) {
  var card = event.target.closest && event.target.closest(".search-help-card");
  if (!card || event.target.closest("a, button")) return;
  var links = card.querySelectorAll("a[href]");
  if (links.length === 1) links[0].click();
});
